<template>
	<layout-floated-button
		class="activity-bell"
		:class="{ ringing }"
		title="Journal d'activité"
		:badge="unreadCount > 0 ? unreadCount : null"
		@click="onClick">
		<cfl-icon icon="menu-activities"></cfl-icon>
	</layout-floated-button>
</template>

<script lang="ts" setup>
	import { onMounted, ref } from "vue";
	import CflIcon from "@shared-components-cfl/cfl-icon.vue";
	import LayoutFloatedButton from "@shared-components/layout/layout-floated-button.vue";
	import usePubSub from "@shared-composables/communication/usePubSub";
	import PubSubEventEnum from "@shared-enums/communication/pub.sub.event.enum";
	import { getActivityLogHistory } from "@/services/profile/activity.log.service";
	import { useRouter } from "vue-router";
	import useToastError from "@shared-composables/useToastError";

	const LAST_SEEN_KEY = "admin-activity-bell-last-seen-id";
	const RING_DURATION_MS = 1000;

	const router = useRouter();

	const lastSeenId = ref<string | null>(localStorage.getItem(LAST_SEEN_KEY));
	const latestId = ref<string | null>(null);
	const unreadCount = ref<number>(0);
	const ringing = ref<boolean>(false);

	let ringTimer: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Une activité arrive : la cloche sonne et le compteur monte.
	 *
	 * On ne garde que l'identifiant le plus récent, pas la liste : le détail se lit sur
	 * `/activities`, qui le recharge depuis l'API.
	 */
	const onNewActivity = (payload: { data: { id?: string } }): void =>
	{
		latestId.value = payload.data?.id ?? latestId.value;
		unreadCount.value += 1;

		if (ringTimer)
		{
			clearTimeout(ringTimer);
		}

		ringing.value = false;
		requestAnimationFrame(() =>
		{
			ringing.value = true;
			ringTimer = setTimeout(() => { ringing.value = false; }, RING_DURATION_MS);
		});
	};

	const onClick = (): void =>
	{
		if (latestId.value)
		{
			lastSeenId.value = latestId.value;
			localStorage.setItem(LAST_SEEN_KEY, latestId.value);
		}

		unreadCount.value = 0;
		router.push("/activities");
	};

	usePubSub(PubSubEventEnum.ADMIN_ACTIVITY_LOG_CREATE, onNewActivity);

	onMounted(async () =>
	{
		try
		{
			const response = await getActivityLogHistory(null, null, [], "30");
			// L'API retourne { data: Record<date, ActivityLog[]>, total }. On aplatit.
			const flat = Object.values(response.data ?? {}).flat();

			latestId.value = flat[0]?.id ?? null;

			const seenIndex = lastSeenId.value ? flat.findIndex(activity => activity.id === lastSeenId.value) : -1;
			unreadCount.value = seenIndex === -1 ? flat.length : seenIndex;
		}
		catch (e)
		{
			useToastError(e);
		}
	});
</script>

<style scoped>
	@reference "@main-css";

	.activity-bell {
		&.ringing {
			&:deep([data-icon]) {
				animation: ring 1s ease-in-out 1;
				transform-origin: top center;
			}
		}
	}

	@keyframes ring {
		0% {
			transform: rotate(0deg);
		}
		12.5% {
			transform: rotate(15deg);
		}
		25% {
			transform: rotate(-15deg);
		}
		37.5% {
			transform: rotate(12deg);
		}
		50% {
			transform: rotate(-12deg);
		}
		62.5% {
			transform: rotate(8deg);
		}
		75% {
			transform: rotate(-8deg);
		}
		87.5% {
			transform: rotate(4deg);
		}
		100% {
			transform: rotate(0deg);
		}
	}
</style>
